import {useContracts, useContractTypes, useDealers, useLegacyContracts} from '@api'
import {ContractStatus, FormFieldType, NavigationPathKey, TranslationKey} from '@enums'
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterInitialState,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from '@mui/x-data-grid'
import {pageLinksAtom, getUserSelectionAtom, getUserTimeFormatAtom} from '@state'

import {useEffect, useMemo, useState} from 'react'

import {getStatusCell, getUserData, isCompanyUser, renderClickableCell, useViewNavigate} from 'common'
import {compareAsc, formatDate, parse} from 'date-fns'
import {useAtomValue, useSetAtom} from 'jotai'
import {useTranslation} from 'react-i18next'

import {ComposedComponents, UIKit} from '@components'

export interface ContractTableData {
  status: ContractStatus
  contractType: string
  customerType: string
  dealer: string
  customer: string
  date: string
  id: string
  contractCode: string
}

export interface LegacyTableData {
  companyNumber: number
  contractId: string
  dealer: string
  contractNumber: string
  supplierName: string
  customerName: string
  contractTypeNumber: string
  contractStatus: string
  contractLength: string
  currentPeriod: string
}

export enum ContractSearchTab {
  CONTRACTS,
  QUOTES,
  LEGACY,
}

export enum LegacyTab {
  FLOORPLAN_DEMO,
  OP_LEASE,
}

export function useContractSearchModel() {
  const setTitleLinks = useSetAtom(pageLinksAtom)
  const {t} = useTranslation()
  const [activeTab, setActiveTab] = useState<ContractSearchTab>(ContractSearchTab.CONTRACTS)
  const [activeLegacyTab, setActiveLegacyTab] = useState<LegacyTab>(LegacyTab.OP_LEASE)

  const [tableFilterModel, setTableFilterModel] = useState<GridFilterInitialState | undefined>(undefined)

  const navigate = useViewNavigate()

  const currentUser = getUserData()
  const userSelections = useAtomValue(getUserSelectionAtom)
  const getUserTimeFormat = useAtomValue(getUserTimeFormatAtom)
  const userSelection = userSelections[currentUser.id]

  const {data: contractsResponse} = useContracts()
  const userTimeFormat = getUserTimeFormat(currentUser.id)

  const {data: legacyContractsResponse} = useLegacyContracts()
  const {data: dealersData} = useDealers()
  const {data: contractTypesData} = useContractTypes()

  const legacyRows = useMemo(() => {
    if (!legacyContractsResponse)
      return {
        [LegacyTab.FLOORPLAN_DEMO]: [],
        [LegacyTab.OP_LEASE]: [],
      }
    const formatted = legacyContractsResponse
      .map(item => ({
        ...item,
        id: item.contractId,
        dealer: item.supplierName,
      }))
      .filter(item =>
        userSelection?.dealerId && userSelection.dealerId !== 'all' && dealersData
          ? item.dealer === dealersData.find(dealer => dealer.id === userSelection.dealerId)?.name
          : true,
      )

    return {
      [LegacyTab.FLOORPLAN_DEMO]: formatted.filter(item => ['666', '667'].includes(item.contractTypeNumber)),
      [LegacyTab.OP_LEASE]: formatted.filter(item => !['666', '667'].includes(item.contractTypeNumber)),
    }
  }, [legacyContractsResponse])

  const rows = useMemo(() => {
    if (!contractsResponse || !dealersData) return {contracts: [], quotes: []}
    else {
      const filteredRows = contractsResponse
        ?.filter(item => item.companyId === userSelection?.companyId)
        .filter(item => (isCompanyUser() ? item.dealerId === userSelection?.dealerId : true))
        .map(item => ({
          contractCode: item.contractCode,
          customer: `${item.customer.firstName} ${item.customer.lastName}`,
          dealer: dealersData?.find(dealer => dealer.id === item.dealerId)?.name || '',
          contractType: contractTypesData?.find(contractType => contractType.id === item.contractTypeId)?.name || '',
          customerType: contractTypesData?.find(contractType => contractType.id === item.contractTypeId)?.type || '',
          status: item.status,
          date: formatDate(item.createdAt, userTimeFormat),
          id: item.id || '',
        }))
        .sort((a, b) =>
          compareAsc(parse(b.date, userTimeFormat, new Date()), parse(a.date, userTimeFormat, new Date())),
        )

      return {
        contracts: filteredRows
          .filter(item => item.status !== ContractStatus.QUOTE)
          .filter(item =>
            userSelection?.dealerId && userSelection.dealerId !== 'all' && dealersData
              ? item.dealer === dealersData.find(dealer => dealer.id === userSelection.dealerId)?.name
              : true,
          ),
        quotes: filteredRows
          .filter(item => item.status === ContractStatus.QUOTE)
          .filter(item =>
            userSelection?.dealerId && userSelection.dealerId !== 'all' && dealersData
              ? item.dealer === dealersData.find(dealer => dealer.id === userSelection.dealerId)?.name
              : true,
          ),
      }
    }
  }, [contractsResponse, dealersData])

  useEffect(() => {
    if (userSelection?.dealerId && userSelection.dealerId !== 'all' && dealersData)
      setTableFilterModel({
        filterModel: {
          items: [
            {
              id: 1,
              field: 'dealer',
              operator: 'contains',
              value: dealersData?.find(item => item.id === userSelection.dealerId)?.name,
            },
          ],
        },
      })
  }, [userSelection, dealersData])

  useEffect(() => setTitleLinks([{title: t(TranslationKey['Contract search'])}]), [])

  /**
   * Tab buttons configuration
   */
  const tabButtons: ComposedComponents.TabButtonProps<ContractSearchTab>[] = [
    {
      label: t(TranslationKey['List of contracts']),
      key: ContractSearchTab.CONTRACTS,
      onClick: () => setActiveTab(ContractSearchTab.CONTRACTS),
    },
    {
      label: t(TranslationKey['List of quotes']),
      key: ContractSearchTab.QUOTES,
      onClick: () => setActiveTab(ContractSearchTab.QUOTES),
    },
    {
      label: t(TranslationKey['All contracts']),
      key: ContractSearchTab.LEGACY,
      onClick: () => setActiveTab(ContractSearchTab.LEGACY),
    },
  ]

  const legacyTabButtons: ComposedComponents.TabButtonProps<LegacyTab>[] = [
    {
      label: 'Op. Lease',
      key: LegacyTab.OP_LEASE,
      onClick: () => setActiveLegacyTab(LegacyTab.OP_LEASE),
    },
    {
      label: 'Floorplan/Demo',
      key: LegacyTab.FLOORPLAN_DEMO,
      onClick: () => setActiveLegacyTab(LegacyTab.FLOORPLAN_DEMO),
    },
  ]

  /**
   * Contract table columns configuration
   */
  const columns: GridColDef[] = [
    {
      field: 'contractCode',
      headerName: t(TranslationKey['Contract code']),
      flex: 1,
      renderCell: params =>
        renderClickableCell(params, () => navigate(`${NavigationPathKey.CONTRACT_VIEW}/${params.row.id}`)),
    },
    {field: 'dealer', headerName: t(TranslationKey.Dealer), flex: 1.2},
    {field: 'customer', headerName: t(TranslationKey.Customer), flex: 1, hideable: false},
    {field: 'contractType', headerName: t(TranslationKey['Contract type']), flex: 1},
    {field: 'customerType', headerName: t(TranslationKey.Type), width: 120},
    {
      field: 'status',
      headerName: t(TranslationKey.Status),
      width: 120,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      renderCell: (params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) =>
        getStatusCell(t, params.value),
    },
    {field: 'date', headerName: t(TranslationKey.Date), width: 120},
  ]

  const legacyColumns: GridColDef[] = [
    {
      field: 'contractNumber',
      headerName: t(TranslationKey['Contract code']),
      flex: 1,
      renderCell: params =>
        renderClickableCell(params, () => navigate(`${NavigationPathKey.CONTRACT_LEGACY_VIEW}/${params.row.id}`)),
    },
    {field: 'dealer', headerName: t(TranslationKey.Dealer), flex: 1.2},
    {field: 'customerName', headerName: t(TranslationKey.Customer), flex: 1.2},
    {field: 'contractLength', headerName: t(TranslationKey['Contract length']), flex: 1.2},
    {field: 'currentPeriod', headerName: t(TranslationKey.Period), flex: 1.2},
    {
      field: 'contractStatus',
      headerName: t(TranslationKey.Status),
      width: 120,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      renderCell: (params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>) =>
        getStatusCell(t, params.value),
    },
  ]

  /**
   * Contract table visibility model configuration
   */
  const visibilityModel: GridColumnVisibilityModel = {
    company: false,
    contractType: false,
  }

  const legacyVisibilityModel: GridColumnVisibilityModel = {}

  const filterConfig: UIKit.FilteringProp[] = [
    {field: 'contractCode', type: FormFieldType.INPUT},
    {field: 'dealer', type: FormFieldType.MULTI_DROPDOWN},
    {field: 'contractType', type: FormFieldType.MULTI_DROPDOWN},
    {field: 'customer', type: FormFieldType.INPUT},
    {field: 'customerType', type: FormFieldType.MULTI_DROPDOWN},
    {field: 'status', type: FormFieldType.MULTI_DROPDOWN},
  ]

  const legacyFilterConfig: UIKit.FilteringProp[] = [
    {field: 'contractNumber', type: FormFieldType.INPUT},
    {field: 'dealer', type: FormFieldType.MULTI_DROPDOWN},
    {field: 'customerName', type: FormFieldType.INPUT},
    {field: 'contractStatus', type: FormFieldType.MULTI_DROPDOWN},
  ]

  return {
    navigate,
    legacyTabButtons,
    activeLegacyTab,
    legacyColumns,
    legacyFilterConfig,
    legacyRows,
    visibilityModel,
    t,
    legacyVisibilityModel,
    tabButtons,
    activeTab,
    rows,
    tableFilterModel,
    columns,
    filterConfig,
  }
}
